import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useZero } from '@/lib/zero';

type PullData = any;
type PullDiffsData = any;

/**
 * Validates that a SHA is properly formatted
 */
function isValidSha(sha: string): boolean {
  if (!sha || typeof sha !== 'string') return false;
  if (sha.length < 10) return false;
  if (sha.startsWith('/')) return false;
  if (sha.includes(' ')) return false;
  return /^[a-f0-9]+$/i.test(sha);
}

/**
 * Validates and sanitizes pull data to prevent corrupted SHA values
 */
function validatePullData(pull: PullData): PullData | null {
  if (!pull) return null;

  // Check if head.sha is corrupted
  if (pull.head?.sha && !isValidSha(pull.head.sha)) {
    console.error('🚨 CORRUPTED SHA DETECTED:', {
      sha: pull.head.sha,
      pullNumber: pull.number,
      owner: pull.owner,
      repo: pull.repo,
      fullPull: pull,
    });
    return null; // Return null to prevent using corrupted data
  }

  return pull;
}

/**
 * Hook that wraps Zero queries for pull data with manual refresh control.
 * Uses reactive queries for initialization (proper timing) and .run() for refresh (explicit fresh data).
 * Prevents automatic re-renders when data updates, instead showing a refresh button
 * to let users control when to apply updates.
 */
export function usePullDataWithRefresh(owner: string, repo: string, number: number) {
  const z = useZero();
  const queryClient = useQueryClient();

  // Use reactive queries ONLY for change detection
  const [latestPull] = useZeroQuery(
    z.query.PullsTable.where('owner', owner).where('repo', repo).where('number', number).one()
  );
  const [latestPullDiffs] = useZeroQuery(
    z.query.PullDiffsTable.where('owner', owner).where('repo', repo).where('number', number).one()
  );

  // Stable data that only updates when user clicks refresh
  const [stablePull, setStablePull] = useState<PullData | null>(null);
  const [stablePullDiffs, setStablePullDiffs] = useState<PullDiffsData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize stable data when first loaded
  useEffect(() => {
    if (latestPull && !stablePull) {
      const validatedPull = validatePullData(latestPull);
      if (validatedPull) {
        setStablePull(validatedPull);
      }
    }
  }, [latestPull, stablePull]);

  useEffect(() => {
    if (latestPullDiffs && !stablePullDiffs) {
      setStablePullDiffs(latestPullDiffs);
    }
  }, [latestPullDiffs, stablePullDiffs]);

  // Auto-update stable data when isGenerating changes from true to false
  // This prevents components from getting stuck in loading state
  useEffect(() => {
    if (stablePullDiffs && latestPullDiffs) {
      // If stable data has isGenerating: true but latest has isGenerating: false
      // AND they're for the same commit, update the stable data
      if (
        stablePullDiffs.isGenerating === true &&
        latestPullDiffs.isGenerating === false &&
        stablePullDiffs.commitSha === latestPullDiffs.commitSha
      ) {
        setStablePullDiffs(latestPullDiffs);
      }
    }
  }, [stablePullDiffs, latestPullDiffs]);

  // Detect if updates are available by comparing stable vs latest data
  const hasUpdates = useMemo(() => {
    if (!stablePull || !latestPull || !stablePullDiffs || !latestPullDiffs) {
      return false;
    }

    // Validate latest pull data before comparing
    const validatedLatestPull = validatePullData(latestPull);
    if (!validatedLatestPull) {
      return false; // Don't show updates if latest data is corrupted
    }

    // Check for meaningful changes (actual data changes, not just isGenerating state)
    const hasContentChanges =
      stablePull.head?.sha !== validatedLatestPull.head?.sha || stablePullDiffs.commitSha !== latestPullDiffs.commitSha;

    return hasContentChanges;
  }, [stablePull, latestPull, stablePullDiffs, latestPullDiffs]);

  // Refresh function that applies latest data and forces remount
  const refresh = useCallback(async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    // First, invalidate React Query cache to prevent stale cache issues
    await queryClient.invalidateQueries({
      queryKey: [owner, repo, 'rawFile'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: [owner, repo, 'pull', number, 'files'],
      exact: false,
    });

    try {
      // Use .run({type: 'complete'}) to ensure we get fresh server data
      const [pull, pullDiffs] = await Promise.all([
        z.query.PullsTable.where('owner', owner)
          .where('repo', repo)
          .where('number', number)
          .one()
          .run({ type: 'complete' }),
        z.query.PullDiffsTable.where('owner', owner)
          .where('repo', repo)
          .where('number', number)
          .one()
          .run({ type: 'complete' }),
      ]);

      // 🔍 DEBUG: Log the data before setting state
      console.log('🔍 Fresh pull data:', {
        pullHeadSha: pull?.head?.sha,
        pullHeadRef: pull?.head?.ref,
        pullNumber: pull?.number,
        pullDiffsCommitSha: pullDiffs?.commitSha,
      });

      // Validate pull data before updating state
      const validatedPull = validatePullData(pull);
      if (validatedPull) {
        setStablePull(validatedPull);
      } else {
        console.error('🚨 Refresh failed: Fresh pull data is corrupted');
        throw new Error('Fresh pull data is corrupted');
      }

      if (pullDiffs) setStablePullDiffs(pullDiffs);

      // Always increment key to force component remount
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to refresh pull data:', error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, z, owner, repo, number, queryClient]);

  return {
    pull: stablePull,
    pullDiffs: stablePullDiffs,
    hasUpdates,
    refresh,
    refreshKey,
    isRefreshing,
  };
}
